// src/components/POS/POS.js
// Permission-based access control for all POS sections
// PHARMACYBRANCH has FULL ACCESS to all sections
import React, { useState, useEffect, useRef } from "react";
import "./POS.css";
import Sidebar from "../Sidebar/Sidebar";
import api, { cloudApi } from "../../api";
import { useNavigate } from "react-router-dom";
import ReceiptModal from "../ReceiptModal/ReceiptModal";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";

const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getNetworkErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  const isNetworkError = (
    !navigator.onLine ||
    err.code === "ERR_NETWORK" ||
    err.code === "ERR_INTERNET_DISCONNECTED" ||
    err.code === "ERR_NAME_NOT_RESOLVED" ||
    err.code === "ERR_CONNECTION_REFUSED" ||
    err.code === "ERR_CONNECTION_TIMED_OUT" ||
    err.message === "Network Error" ||
    err.message?.includes("ERR_INTERNET_DISCONNECTED") ||
    err.message?.includes("net::")
  );
  if (isNetworkError) {
    if (IS_DESKTOP) return "🔧 Cannot connect to local server. Please restart the MyApoti desktop app.";
    return "📡 No internet connection. Please check your network and try again.";
  }
  if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
    return "⏱ Request timed out. The server took too long to respond. Please try again.";
  }
  if (err.response) {
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (status === 401) return "🔒 Session expired. Please log in again.";
    if (status === 403) return `🚫 Access denied. ${detail || "You don't have permission for this action."}`;
    if (status === 404) return `🔍 Not found. ${detail || "The requested resource was not found."}`;
    if (status === 409) return `⚠️ Conflict. ${detail || "This record already exists."}`;
    if (status === 422) return `❌ Invalid data. ${detail || "Please check your input and try again."}`;
    if (status === 429) return "⏳ Too many requests. Please wait a moment and try again.";
    if (status >= 500) return `🔧 Server error (${status}). Please try again later or contact support.`;
    if (detail) return detail;
  }
  return fallback;
};

const calculateDiscount = (amount, discountType, discountValue) => {
  if (!discountType || !discountValue || parseFloat(discountValue) <= 0) {
    return { discountAmount: 0, finalAmount: amount };
  }
  let discountAmount = 0;
  if (discountType === "percentage") {
    const pct = Math.min(parseFloat(discountValue), 100);
    discountAmount = parseFloat((amount * pct / 100).toFixed(2));
  } else if (discountType === "fixed") {
    discountAmount = Math.min(parseFloat(discountValue), amount);
    discountAmount = parseFloat(discountAmount.toFixed(2));
  }
  const finalAmount = parseFloat(Math.max(amount - discountAmount, 0).toFixed(2));
  return { discountAmount, finalAmount };
};

const POS = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [inventory, setInventory] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const debounceRef = useRef(null);
  const conflictPollRef = useRef(null);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [receipt, setReceipt] = useState(null);

  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");

  const [userPermissions, setUserPermissions] = useState([]);
  const [userType, setUserType] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState("");

  const [activeView, setActiveView] = useState("pos");

  const [profitPeriod, setProfitPeriod] = useState("daily");
  const [profitData, setProfitData] = useState(null);
  const [profitLoading, setProfitLoading] = useState(false);
  const [profitError, setProfitError] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(12);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [dateFilter, setDateFilter] = useState("");
  const [receiptFilter, setReceiptFilter] = useState("");
  const [drugNameFilter, setDrugNameFilter] = useState("");
  const [voidFilter, setVoidFilter] = useState("all");

  const [outOfStock, setOutOfStock] = useState([]);
  const [outOfStockLoading, setOutOfStockLoading] = useState(false);
  const [outOfStockError, setOutOfStockError] = useState("");
  const [outOfStockPage, setOutOfStockPage] = useState(1);
  const [outOfStockLimit] = useState(12);
  const [outOfStockTotal, setOutOfStockTotal] = useState(0);

  const [lowStock, setLowStock] = useState([]);
  const [lowStockLoading, setLowStockLoading] = useState(false);
  const [lowStockError, setLowStockError] = useState("");
  const [lowStockPage, setLowStockPage] = useState(1);
  const [lowStockLimit] = useState(12);
  const [lowStockTotal, setLowStockTotal] = useState(0);
  const [lowStockThreshold] = useState(10);

  const [expiringSoon, setExpiringSoon] = useState([]);
  const [expiringSoonLoading, setExpiringSoonLoading] = useState(false);
  const [expiringSoonError, setExpiringSoonError] = useState("");
  const [expiringSoonPage, setExpiringSoonPage] = useState(1);
  const [expiringSoonLimit] = useState(12);
  const [expiringSoonTotal, setExpiringSoonTotal] = useState(0);
  const [expiryThreshold] = useState(90);

  const [conflicts, setConflicts] = useState([]);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [conflictsError, setConflictsError] = useState("");
  const [conflictCount, setConflictCount] = useState(0);
  const [showResolvedConflicts, setShowResolvedConflicts] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({ visible: false, title: "", message: "", onConfirm: null });
  const [alertDialog, setAlertDialog] = useState({ visible: false, title: "", message: "", onClose: null });
  const [syncAlert, setSyncAlert] = useState(null);

  const hasPermission = (permissionKey) =>
    userPermissions.some((perm) => perm.toLowerCase() === permissionKey.toLowerCase());
  const checkMultiplePermissions = (permissions) =>
    permissions.some((perm) => hasPermission(perm));

  const isPharmacyBranch = userType === "PHARMACYBRANCH";
  const isPharmacyOwner  = userType === "PHARMACY";
  const showConflicts    = isPharmacyOwner || isPharmacyBranch;
  const canApplyDiscount = isPharmacyOwner || isPharmacyBranch || hasPermission("discount");

  const showConfirm = (title, message) => new Promise((resolve) => {
    setConfirmDialog({ visible: true, title, message, onConfirm: (result) => { setConfirmDialog({ visible: false, title: "", message: "", onConfirm: null }); resolve(result); } });
  });
  const showAlert = (title, message) => new Promise((resolve) => {
    setAlertDialog({ visible: true, title, message, onClose: () => { setAlertDialog({ visible: false, title: "", message: "", onClose: null }); resolve(); } });
  });

  const ErrorBanner = ({ message, onDismiss }) => {
    if (!message) return null;
    return (
      <div style={{ background: "#fff5f5", border: "1px solid #dc3545", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", fontSize: "0.9rem", color: "#dc3545" }}>
        <span>{message}</span>
        {onDismiss && <button onClick={onDismiss} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc3545", fontSize: "1.1rem", lineHeight: 1, padding: 0 }}>✕</button>}
      </div>
    );
  };

  useEffect(() => { const token = localStorage.getItem("access_token"); if (!token) navigate("/Login"); }, [navigate]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setPermissionsLoading(true);
        setPermissionsError("");
        const token = localStorage.getItem("access_token");
        if (token) {
          const { jwtDecode } = await import("jwt-decode");
          const decoded = jwtDecode(token);
          const normalizedType = (decoded.user_type?.value || decoded.user_type || "").toString().trim();
          setUserType(normalizedType);
        }
        const res = await api.get("/staffmanager/my-permissions");
        setUserPermissions(res.data || []);
      } catch (err) {
        const isNetworkErr = (!navigator.onLine || err.code === "ERR_NETWORK" || err.code === "ERR_INTERNET_DISCONNECTED" || err.code === "ERR_NAME_NOT_RESOLVED" || err.code === "ERR_CONNECTION_REFUSED" || err.code === "ERR_CONNECTION_TIMED_OUT" || err.message === "Network Error" || err.message?.includes("ERR_INTERNET_DISCONNECTED") || err.message?.includes("net::"));
        setPermissionsError(isNetworkErr ? (IS_DESKTOP ? "🔧 Cannot connect to local server. Please restart the MyApoti desktop app." : "📡 No internet connection. Please check your network and try again.") : "Failed to load permissions. Please refresh the page.");
        setUserPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };
    fetchUserInfo();
  }, []);

  useEffect(() => { const handleResize = () => setIsMobile(window.innerWidth <= 768); window.addEventListener("resize", handleResize); return () => window.removeEventListener("resize", handleResize); }, []);
  useEffect(() => { if (isPharmacyBranch || hasPermission("pos_access")) fetchInventory(""); }, [userPermissions, isPharmacyBranch]);
  useEffect(() => { return () => { if (debounceRef.current) clearTimeout(debounceRef.current); if (conflictPollRef.current) clearInterval(conflictPollRef.current); }; }, []);

  useEffect(() => {
    if (!showConflicts) return;
    const pollConflicts = async () => { try { const res = await cloudApi.get("/POS/pos/conflicts/summary"); setConflictCount(res.data.unresolved_conflicts || 0); } catch {} };
    pollConflicts();
    conflictPollRef.current = setInterval(pollConflicts, 2 * 60 * 1000);
    return () => clearInterval(conflictPollRef.current);
  }, [showConflicts]);

  useEffect(() => {
    if (!IS_DESKTOP) return;
    let interval;
    const checkSyncAlert = async () => {
      try {
        const res = await api.get("/sync/pending-alert");
        if (res.data.alert) { setSyncAlert(res.data); clearInterval(interval); interval = setInterval(checkSyncAlert, 30 * 1000); }
        else { setSyncAlert(null); clearInterval(interval); interval = setInterval(checkSyncAlert, 5 * 60 * 1000); }
      } catch {}
    };
    checkSyncAlert();
    interval = setInterval(checkSyncAlert, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchInventory = async (query = "") => {
    try {
      setLoading(true); setError("");
      const params = { page: 1, limit: 100 };
      if (query.trim()) params.query = query.trim();
      const res = await api.get("/pharmacies/get_inventory", { params });
      setInventory(res.data.data || []);
    } catch (err) {
      setError(getNetworkErrorMessage(err, "Failed to load inventory. Please try again."));
    } finally { setLoading(false); }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchInventory(value), 400);
  };

  const getEffectivePrice = (item) =>
    item.priceType === "wholesale" && item.wholesale_price ? item.wholesale_price : item.price;

  const addToCart = (item) => {
    const existing = cart.find((c) => c.id === item.id);
    if (existing) { setCart(cart.map((c) => c.id === item.id ? { ...c, qty: c.qty + 1 } : c)); }
    else { setCart([...cart, { ...item, qty: 1, priceType: "retail" }]); }
  };
  const removeFromCart = (id) => setCart(cart.filter((c) => c.id !== id));
  const updateQty = (id, value) => {
    if (value === "" || value === "0") { setCart(cart.map((c) => c.id === id ? { ...c, qty: value } : c)); return; }
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1) return;
    setCart(cart.map((c) => c.id === id ? { ...c, qty: parsed } : c));
  };
  const togglePriceType = (id) => {
    setCart(cart.map((c) => c.id !== id ? c : { ...c, priceType: c.priceType === "retail" ? "wholesale" : "retail" }));
  };

  const subtotal = cart.reduce((sum, item) => {
    const qty = parseInt(item.qty, 10);
    return sum + getEffectivePrice(item) * (isNaN(qty) || qty < 1 ? 0 : qty);
  }, 0);

  const { discountAmount, finalAmount: totalAfterDiscount } = canApplyDiscount
    ? calculateDiscount(subtotal, discountValue ? discountType : null, discountValue)
    : { discountAmount: 0, finalAmount: subtotal };

  const displayTotal = canApplyDiscount && discountAmount > 0 ? totalAfterDiscount : subtotal;

  const handleCheckout = async () => {
    if (cart.length === 0) { setCheckoutError("Cart is empty. Add items before checking out."); return; }
    if (checkoutLoading) return;
    const invalidItems = cart.filter((c) => { const qty = parseInt(c.qty, 10); return isNaN(qty) || qty < 1; });
    if (invalidItems.length > 0) { setCheckoutError("Please enter a valid quantity (at least 1) for all items in the cart."); return; }
    if (canApplyDiscount && discountValue && parseFloat(discountValue) > 0) {
      if (discountType === "percentage" && parseFloat(discountValue) > 100) { setCheckoutError("Percentage discount cannot exceed 100%."); return; }
      if (discountType === "fixed" && parseFloat(discountValue) > subtotal) { setCheckoutError("Fixed discount cannot exceed the total amount."); return; }
    }
    setCheckoutError("");
    const transaction_id = generateUUID();
    const hasDiscount = canApplyDiscount && discountValue && parseFloat(discountValue) > 0;
    const payload = {
      transaction_id,
      items: cart.map((c) => ({
        inventory_id: c.id,
        quantity: parseInt(c.qty, 10),
        price: getEffectivePrice(c),
        cost_price: c.cost_price || null,
        price_type: c.priceType || "retail",
      })),
      discount_type:   hasDiscount ? discountType : null,
      discount_value:  hasDiscount ? parseFloat(discountValue) : null,
      subtotal:        parseFloat(subtotal.toFixed(2)),
      discount_amount: hasDiscount ? discountAmount : null,
      total_amount:    parseFloat(displayTotal.toFixed(2)),
    };
    try {
      setCheckoutLoading(true);
      const res = await api.post("/POS/pos/checkout", payload);
      const backendReceipt = res.data.receipt;
      setReceipt({ ...backendReceipt, receipt_number: backendReceipt.receipt_number || backendReceipt.sale_id });
      setCart([]);
      setDiscountValue("");
      setDiscountType("percentage");
      fetchInventory(search);
      if (IS_DESKTOP) { try { const alertRes = await api.get("/sync/pending-alert"); setSyncAlert(alertRes.data.alert ? alertRes.data : null); } catch {} }
    } catch (err) {
      setCheckoutError(getNetworkErrorMessage(err, "Checkout failed. Please try again."));
    } finally { setCheckoutLoading(false); }
  };

  const fetchConflicts = async (resolved = false) => {
    try { setConflictsLoading(true); setConflictsError(""); const res = await cloudApi.get("/POS/pos/conflicts", { params: { resolved } }); setConflicts(res.data.conflicts || []); }
    catch (err) { setConflictsError(getNetworkErrorMessage(err, "Failed to load stock conflicts.")); }
    finally { setConflictsLoading(false); }
  };

  const handleResolveConflict = async (saleId, receiptNumber) => {
    const confirmed = await showConfirm("✅ Resolve Conflict", `Mark conflict for receipt ${receiptNumber} as resolved?\n\nMake sure you have done a physical recount and updated the stock manually before dismissing.`);
    if (!confirmed) return;
    try {
      setResolvingId(saleId);
      await cloudApi.post(`/POS/pos/conflicts/${saleId}/resolve`);
      await fetchConflicts(showResolvedConflicts);
      const summaryRes = await cloudApi.get("/POS/pos/conflicts/summary");
      setConflictCount(summaryRes.data.unresolved_conflicts || 0);
      await showAlert("✅ Conflict Resolved", "Conflict marked as resolved. Stock has been updated manually.");
    } catch (err) { setConflictsError(getNetworkErrorMessage(err, "Failed to resolve conflict.")); }
    finally { setResolvingId(null); }
  };

  const fetchSalesHistory = async (page = 1, date = "", receiptId = "", drugName = "", voidStatus = "all") => {
    try {
      setHistoryLoading(true); setHistoryError("");
      const skip = (page - 1) * historyLimit;
      const res = await api.get("/POS/pos/sales/history", { params: { skip, limit: historyLimit } });
      let salesData = res.data.sales || [];
      if (date) salesData = salesData.filter((sale) => new Date(sale.created_at).toISOString().split("T")[0] === date);
      if (receiptId) salesData = salesData.filter((sale) => sale.receipt_number?.toLowerCase().includes(receiptId.toLowerCase()));
      if (drugName) salesData = salesData.filter((sale) => sale.items?.some((item) => item.name?.toLowerCase().includes(drugName.toLowerCase())));
      if (voidStatus === "active") salesData = salesData.filter((sale) => sale.status !== "voided");
      else if (voidStatus === "voided") salesData = salesData.filter((sale) => sale.status === "voided");
      setHistory(salesData); setHistoryTotal(salesData.length);
    } catch (err) { setHistoryError(getNetworkErrorMessage(err, "Failed to load sales history.")); }
    finally { setHistoryLoading(false); }
  };

  const fetchOutOfStock = async (page = 1) => {
    try {
      setOutOfStockLoading(true); setOutOfStockError("");
      const res = await api.get("/pharmacies/get_inventory", { params: { page, limit: 100 } });
      const filtered = (res.data.data || []).filter((item) => item.quantity === 0);
      const start = (page - 1) * outOfStockLimit;
      setOutOfStock(filtered.slice(start, start + outOfStockLimit)); setOutOfStockTotal(filtered.length);
    } catch (err) { setOutOfStockError(getNetworkErrorMessage(err, "Failed to load out of stock items.")); }
    finally { setOutOfStockLoading(false); }
  };

  const fetchLowStock = async (page = 1) => {
    try {
      setLowStockLoading(true); setLowStockError("");
      const res = await api.get("/pharmacies/get_inventory", { params: { page, limit: 100 } });
      const filtered = (res.data.data || []).filter((item) => item.quantity > 0 && item.quantity <= lowStockThreshold);
      const start = (page - 1) * lowStockLimit;
      setLowStock(filtered.slice(start, start + lowStockLimit)); setLowStockTotal(filtered.length);
    } catch (err) { setLowStockError(getNetworkErrorMessage(err, "Failed to load low stock items.")); }
    finally { setLowStockLoading(false); }
  };

  const fetchExpiringSoon = async (page = 1) => {
    try {
      setExpiringSoonLoading(true); setExpiringSoonError("");
      const res = await api.get("/pharmacies/get_inventory", { params: { page, limit: 100 } });
      const today = new Date();
      const threshold = new Date(today.getTime() + expiryThreshold * 24 * 60 * 60 * 1000);
      const filtered = (res.data.data || [])
        .filter((item) => { const d = item.expiration_date || item.expiry_date; if (!d) return false; const ed = new Date(d); return ed > today && ed <= threshold; })
        .sort((a, b) => new Date(a.expiration_date || a.expiry_date) - new Date(b.expiration_date || b.expiry_date));
      const start = (page - 1) * expiringSoonLimit;
      setExpiringSoon(filtered.slice(start, start + expiringSoonLimit)); setExpiringSoonTotal(filtered.length);
    } catch (err) { setExpiringSoonError(getNetworkErrorMessage(err, "Failed to load expiring items.")); }
    finally { setExpiringSoonLoading(false); }
  };

  const handleVoidReceipt = async (saleId, receiptNumber) => {
    if (!isPharmacyBranch && !hasPermission("pos:void_receipt")) { await showAlert("🔒 Permission Denied", "You don't have permission to void receipts.\n\nRequired Permission: POS_VOID_RECEIPT"); return; }
    const confirmed = await showConfirm("✖ Void Receipt", `Are you sure you want to VOID receipt ${receiptNumber}? This action cannot be undone.`);
    if (!confirmed) return;
    try {
      setHistoryLoading(true);
      await api.post(`/POS/pos/sales/${saleId}/void`);
      await showAlert("✅ Receipt Voided", `Receipt ${receiptNumber} has been voided successfully.`);
      fetchSalesHistory(historyPage, dateFilter, receiptFilter, drugNameFilter, voidFilter);
    } catch (err) { setHistoryError(getNetworkErrorMessage(err, "Failed to void receipt.")); }
    finally { setHistoryLoading(false); }
  };

  const fetchProfitAnalytics = async (period = "daily", startDate = "", endDate = "") => {
    try {
      setProfitLoading(true); setProfitError("");
      const res = await api.get("/POS/pos/sales/history", { params: { skip: 0, limit: 1000 } });
      const salesData = res.data.sales || [];
      const today = new Date();
      let filtered = [];
      if (period === "daily") filtered = salesData.filter((s) => new Date(s.created_at).toDateString() === today.toDateString());
      else if (period === "monthly") filtered = salesData.filter((s) => { const d = new Date(s.created_at); return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); });
      else if (period === "yearly") filtered = salesData.filter((s) => new Date(s.created_at).getFullYear() === today.getFullYear());
      else if (period === "custom" && startDate && endDate) {
        const start = new Date(startDate); start.setHours(0, 0, 0, 0);
        const end = new Date(endDate); end.setHours(23, 59, 59, 999);
        filtered = salesData.filter((s) => { const d = new Date(s.created_at); return d >= start && d <= end; });
      }
      const totalRevenue = filtered.reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0);
      const totalCost = filtered.reduce((sum, s) => sum + (s.total_cost || 0), 0);
      const totalProfit = filtered.reduce((sum, s) => sum + (s.total_profit || 0), 0);
      const totalDiscount = filtered.reduce((sum, s) => sum + parseFloat(s.discount_amount || 0), 0);
      const totalTransactions = filtered.length;
      const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
      const itemSales = {};
      filtered.forEach((sale) => { sale.items.forEach((item) => { if (!itemSales[item.name]) itemSales[item.name] = { name: item.name, quantity: 0, revenue: 0, profit: 0 }; itemSales[item.name].quantity += item.quantity; itemSales[item.name].revenue += item.subtotal || 0; itemSales[item.name].profit += item.profit || 0; }); });
      const topItems = Object.values(itemSales).sort((a, b) => b.profit - a.profit).slice(0, 10);
      setProfitData({ period, totalSales: totalRevenue, totalCost, totalProfit, totalDiscount, totalTransactions, profitMargin, topItems, sales: filtered, startDate, endDate });
    } catch (err) { setProfitError(getNetworkErrorMessage(err, "Failed to load profit analytics.")); }
    finally { setProfitLoading(false); }
  };

  const handleViewChange = async (view) => {
    if (!isPharmacyBranch) {
      const viewPermissions = { pos: ["pos_access"], history: ["sales:history"], reports: ["sales:report"], profitanalytics: ["profit:analytics"], lowstock: ["inventory:low_stock"], outofstock: ["inventory:out_of_stock"], expiring: ["inventory:expiring"], conflicts: ["pos_access"] };
      const requiredPerms = viewPermissions[view] || [];
      if (view !== "conflicts" && !checkMultiplePermissions(requiredPerms)) { await showAlert("🔒 Access Denied", `You don't have permission to access this section.\n\nRequired Permission: ${requiredPerms.join(", ")}`); return; }
    }
    setActiveView(view); setError("");
    if (view === "history") { setHistoryPage(1); setDateFilter(""); setReceiptFilter(""); setDrugNameFilter(""); setVoidFilter("all"); fetchSalesHistory(1, "", "", "", "all"); }
    if (view === "outofstock") { setOutOfStockPage(1); fetchOutOfStock(1); }
    if (view === "lowstock") { setLowStockPage(1); fetchLowStock(1); }
    if (view === "expiring") { setExpiringSoonPage(1); fetchExpiringSoon(1); }
    if (view === "profitanalytics") { setProfitPeriod("daily"); fetchProfitAnalytics("daily"); }
    if (view === "conflicts") { setShowResolvedConflicts(false); fetchConflicts(false); }
  };

  const handleFilterChange = () => { setHistoryPage(1); fetchSalesHistory(1, dateFilter, receiptFilter, drugNameFilter, voidFilter); };
  const handleClearFilters = () => { setDateFilter(""); setReceiptFilter(""); setDrugNameFilter(""); setVoidFilter("all"); setHistoryPage(1); fetchSalesHistory(1, "", "", "", "all"); };
  const handleHistoryPageChange = (p) => { setHistoryPage(p); fetchSalesHistory(p, dateFilter, receiptFilter, drugNameFilter, voidFilter); };
  const handleOutOfStockPageChange = (p) => { setOutOfStockPage(p); fetchOutOfStock(p); };
  const handleLowStockPageChange = (p) => { setLowStockPage(p); fetchLowStock(p); };
  const handleExpiringSoonPageChange = (p) => { setExpiringSoonPage(p); fetchExpiringSoon(p); };

  const displayDrugName = (item) => {
    if (item.brand_name && item.generic_name) return `${item.brand_name} (${item.generic_name})`;
    return item.brand_name || item.generic_name || "Unnamed Drug";
  };
  const calculateDaysUntilExpiry = (expiryDate) => { if (!expiryDate) return null; return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24)); };
  const getExpiryStatusColor = (days) => { if (days <= 30) return "#dc3545"; if (days <= 60) return "#ff9800"; return "#ffc107"; };
  const handleProfitPeriodChange = (period) => { setProfitPeriod(period); if (period !== "custom") fetchProfitAnalytics(period); };
  const handleCustomDateSearch = async () => {
    if (!customStartDate || !customEndDate) { await showAlert("📅 Date Required", "Please select both start and end dates."); return; }
    if (new Date(customStartDate) > new Date(customEndDate)) { await showAlert("📅 Invalid Range", "Start date must be before end date."); return; }
    fetchProfitAnalytics("custom", customStartDate, customEndDate);
  };
  const getPeriodLabel = (period) => {
    if (period === "daily") return "Today";
    if (period === "monthly") return "This Month";
    if (period === "yearly") return "This Year";
    if (period === "custom" && customStartDate && customEndDate) return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
    return "Custom Range";
  };

  if (permissionsLoading) {
    return (<div className="pos-layout">{!isMobile && <Sidebar />}<div className="pos-container"><p>Loading permissions...</p></div></div>);
  }

  if (permissionsError) {
    return (
      <div className="pos-layout">
        {!isMobile && <Sidebar />}
        <div className="pos-container">
          <div style={{ background: "#fff5f5", border: "1px solid #dc3545", borderRadius: "8px", padding: "2rem", textAlign: "center", marginTop: "2rem" }}>
            <p style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>{IS_DESKTOP ? "🔧" : "📡"}</p>
            <h3 style={{ color: "#dc3545", marginBottom: "0.5rem" }}>Connection Error</h3>
            <p style={{ color: "#555", marginBottom: "1.5rem" }}>{permissionsError}</p>
            <button onClick={() => { setPermissionsError(""); setPermissionsLoading(true); window.location.reload(); }} style={{ padding: "0.6rem 1.5rem", background: "#dc3545", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "0.95rem" }}>🔄 Retry</button>
          </div>
        </div>
      </div>
    );
  }

  if (!isPharmacyBranch && !hasPermission("pos_access")) {
    return (
      <div className="pos-layout">
        {!isMobile && <Sidebar />}
        <div className="pos-container">
          <div className="access-denied-message">
            <h2>🔒 Access Denied</h2>
            <p>You don't have permission to access the POS section.</p>
            <p><strong>Required Permission:</strong> POS_ACCESS</p>
            <p>Contact your pharmacy owner to request access.</p>
            <button onClick={() => navigate("/dashboard")} className="nav-btn">← Back to Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pos-layout">
      {!isMobile && <Sidebar />}
      <div className="pos-container">

        {/* ── Alert Modal ── */}
        {alertDialog.visible && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "#fff", borderRadius: "10px", padding: "2rem", maxWidth: "460px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
              <h3 style={{ margin: "0 0 1rem", color: "#333", fontSize: "1.1rem" }}>{alertDialog.title}</h3>
              <p style={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6, margin: "0 0 1.5rem", whiteSpace: "pre-line" }}>{alertDialog.message}</p>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={alertDialog.onClose} style={{ padding: "0.6rem 1.5rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}>OK</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Confirm Modal ── */}
        {confirmDialog.visible && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "#fff", borderRadius: "10px", padding: "2rem", maxWidth: "460px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
              <h3 style={{ margin: "0 0 1rem", color: "#333", fontSize: "1.1rem" }}>{confirmDialog.title}</h3>
              <p style={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6, margin: "0 0 1.5rem", whiteSpace: "pre-line" }}>{confirmDialog.message}</p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                <button onClick={() => confirmDialog.onConfirm(false)} style={{ padding: "0.6rem 1.25rem", background: "#f8f9fa", color: "#333", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: 500, fontSize: "0.95rem" }}>Cancel</button>
                <button onClick={() => confirmDialog.onConfirm(true)} style={{ padding: "0.6rem 1.25rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}>Confirm</button>
              </div>
            </div>
          </div>
        )}

        <h2>🧾 Point of Sale</h2>

        {/* ── Conflict banner ── */}
        {showConflicts && conflictCount > 0 && (
          <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontSize: "1.3rem" }}>⚠️</span>
              <p style={{ margin: 0, color: "#856404", fontWeight: "500" }}>{conflictCount} offline sale{conflictCount !== 1 ? "s" : ""} exceeded available stock. Please review and recount.</p>
            </div>
            <button onClick={() => handleViewChange("conflicts")} style={{ padding: "0.4rem 1rem", background: "#ffc107", color: "#333", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap", fontWeight: "600" }}>Review Conflicts</button>
          </div>
        )}

        {/* ── Sync alert banner ── */}
        {IS_DESKTOP && syncAlert && (
          <div style={{ background: !navigator.onLine ? "#fff3cd" : "#d1ecf1", border: `1px solid ${!navigator.onLine ? "#ffc107" : "#bee5eb"}`, borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", fontSize: "0.9rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontSize: "1.3rem" }}>{!navigator.onLine ? "⚠️" : "🔄"}</span>
              <div>
                <strong style={{ color: !navigator.onLine ? "#856404" : "#0c5460", display: "block" }}>{!navigator.onLine ? "Transactions Pending Sync — Connect to Internet" : `${syncAlert.pending_count} Transaction(s) Syncing...`}</strong>
                <p style={{ margin: 0, color: !navigator.onLine ? "#856404" : "#0c5460", fontSize: "0.85rem" }}>{syncAlert.message}</p>
              </div>
            </div>
            {!navigator.onLine && (
              <button onClick={async () => { try { await api.post("/sync/now"); const res = await api.get("/sync/pending-alert"); setSyncAlert(res.data.alert ? res.data : null); } catch {} }} style={{ padding: "0.4rem 1rem", background: "#ffc107", color: "#333", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600, whiteSpace: "nowrap" }}>🔄 Retry Sync</button>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="pos-nav-menu">
          <button className={activeView === "pos" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("pos")}>🛒 POS</button>
          {(isPharmacyBranch || hasPermission("sales:history")) && <button className={activeView === "history" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("history")}>📊 Sales History</button>}
          {(isPharmacyBranch || hasPermission("sales:report")) && <button className={activeView === "reports" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("reports")}>📈 Sales Report</button>}
          {(isPharmacyBranch || hasPermission("profit:analytics")) && <button className={activeView === "profitanalytics" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("profitanalytics")}>💰 Profit Analytics</button>}
          {(isPharmacyBranch || hasPermission("inventory:low_stock")) && <button className={activeView === "lowstock" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("lowstock")}>🔔 Low Stock</button>}
          {(isPharmacyBranch || hasPermission("inventory:out_of_stock")) && <button className={activeView === "outofstock" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("outofstock")}>⚠️ Out of Stock</button>}
          {(isPharmacyBranch || hasPermission("inventory:expiring")) && <button className={activeView === "expiring" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("expiring")}>⏰ Expiring Soon</button>}
          {showConflicts && (
            <button className={activeView === "conflicts" ? "nav-btn active" : "nav-btn"} onClick={() => handleViewChange("conflicts")} style={{ position: "relative" }}>
              🔴 Stock Conflicts
              {conflictCount > 0 && <span style={{ position: "absolute", top: "-6px", right: "-6px", background: "#dc3545", color: "white", borderRadius: "50%", width: "18px", height: "18px", fontSize: "0.7rem", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold" }}>{conflictCount > 9 ? "9+" : conflictCount}</span>}
            </button>
          )}
        </div>

        {/* =========================
            POS VIEW
            ========================= */}
        {activeView === "pos" && (
          <>
            <input type="text" placeholder="Search drug name..." value={search} onChange={handleSearchChange} className="pos-search" />
            <ErrorBanner message={error} onDismiss={() => setError("")} />
            <ErrorBanner message={checkoutError} onDismiss={() => setCheckoutError("")} />
            <div className="pos-body">

              {/* ── Inventory list ── */}
              <div className="pos-inventory">
                <h3>Available Drugs</h3>
                {loading && <p>Loading...</p>}
                {!loading && inventory.length === 0 && !error && <p>{search ? `No results for "${search}"` : "No inventory items found."}</p>}
                {inventory.map((item) => (
                  <div key={item.id} className={`pos-item${item.quantity === 0 ? " out-of-stock" : ""}`}>
                    <div>
                      <strong>{displayDrugName(item)}</strong>
                      <p>
                        ₦{item.price}
                        {item.wholesale_price && (
                          <span style={{ marginLeft: "8px", fontSize: "0.78rem", color: "#64b5f6", background: "#1e3a5f", padding: "1px 6px", borderRadius: "4px", fontWeight: 600 }}>
                            W/S: ₦{item.wholesale_price}
                          </span>
                        )}
                        {" | "}
                        {item.quantity === 0
                          ? <span style={{ color: "#dc3545", fontWeight: 600 }}>Out of Stock</span>
                          : `Stock: ${item.quantity}`}
                      </p>
                    </div>
                    <button onClick={() => addToCart(item)} disabled={item.quantity === 0}>{item.quantity === 0 ? "Out" : "Add"}</button>
                  </div>
                ))}
              </div>

              {/* ── Cart ── */}
              <div className="pos-cart">
                <h3>Cart</h3>
                {cart.length === 0 && <p style={{ color: "#aaa" }}>Cart is empty</p>}

                {cart.map((item) => {
                  const effectivePrice = getEffectivePrice(item);
                  const isWholesale = item.priceType === "wholesale";
                  const hasWholesale = !!item.wholesale_price;
                  return (
                    <div key={item.id} className="cart-item">
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: "#fff", fontSize: "0.9rem" }}>{displayDrugName(item)}</strong>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "3px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "0.9rem", color: isWholesale ? "#64b5f6" : "#e0e0e0", fontWeight: 600 }}>
                            ₦{effectivePrice}
                          </span>
                          {hasWholesale && (
                            <button
                              onClick={() => togglePriceType(item.id)}
                              style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: "4px", border: "none", cursor: "pointer", fontWeight: 600, background: isWholesale ? "#1565c0" : "#555", color: isWholesale ? "#fff" : "#ccc", transition: "all 0.2s" }}
                              title={isWholesale ? "Switch to retail price" : "Switch to wholesale price"}
                            >
                              {isWholesale ? "📦 Wholesale" : "🏷 Retail"}
                            </button>
                          )}
                        </div>
                      </div>
                      <input type="number" min="1" value={item.qty} onChange={(e) => updateQty(item.id, e.target.value)} />
                      <button onClick={() => removeFromCart(item.id)}>✖</button>
                    </div>
                  );
                })}

                {/* ── Wholesale summary note ── */}
                {cart.some((c) => c.priceType === "wholesale") && (
                  <div style={{ background: "#1e3a5f", border: "1px solid #1565c0", borderRadius: "6px", padding: "0.5rem 0.75rem", margin: "0.5rem 0", fontSize: "0.82rem", color: "#64b5f6" }}>
                    📦 {cart.filter((c) => c.priceType === "wholesale").length} item(s) at wholesale price
                  </div>
                )}

                {/* ── Discount section ── */}
                {cart.length > 0 && canApplyDiscount && (
                  <div style={{ border: "1px solid #444", borderRadius: "8px", padding: "0.85rem", margin: "0.75rem 0", background: "#2a2a3d" }}>
                    <p style={{ margin: "0 0 0.6rem", fontWeight: 600, fontSize: "0.88rem", color: "#e0e0e0" }}>
                      🏷️ Discount <span style={{ fontWeight: 400, color: "#888" }}>(optional)</span>
                    </p>
                    <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.5rem" }}>
                      <button onClick={() => { setDiscountType("percentage"); setDiscountValue(""); }} style={{ flex: 1, padding: "0.35rem", fontSize: "0.82rem", background: discountType === "percentage" ? "#007bff" : "#3a3a4d", color: "#fff", border: "1px solid #555", borderRadius: "5px", cursor: "pointer", fontWeight: 600 }}>% Percentage</button>
                      <button onClick={() => { setDiscountType("fixed"); setDiscountValue(""); }} style={{ flex: 1, padding: "0.35rem", fontSize: "0.82rem", background: discountType === "fixed" ? "#007bff" : "#3a3a4d", color: "#fff", border: "1px solid #555", borderRadius: "5px", cursor: "pointer", fontWeight: 600 }}>₦ Fixed</button>
                    </div>
                    <input
                      type="number"
                      placeholder={discountType === "percentage" ? "e.g. 10 for 10%" : "e.g. 500"}
                      value={discountValue}
                      min="0"
                      max={discountType === "percentage" ? "100" : subtotal}
                      step="0.01"
                      onChange={(e) => setDiscountValue(e.target.value)}
                      style={{ width: "100%", padding: "0.45rem 0.6rem", border: "1px solid #555", borderRadius: "5px", fontSize: "0.88rem", boxSizing: "border-box", background: "#1e1e2f", color: "#fff" }}
                    />
                    {discountAmount > 0 && <p style={{ color: "#4caf50", fontWeight: 600, fontSize: "0.82rem", margin: "0.4rem 0 0" }}>✅ You save ₦{discountAmount.toFixed(2)}{discountType === "percentage" ? ` (${discountValue}% off)` : " (flat discount)"}</p>}
                    {discountValue && <button onClick={() => setDiscountValue("")} style={{ marginTop: "0.4rem", background: "none", border: "none", color: "#ef5350", cursor: "pointer", fontSize: "0.8rem", padding: 0 }}>✕ Remove discount</button>}
                  </div>
                )}

                {/* ── Order summary ── */}
                {cart.length > 0 && (
                  <div style={{ borderTop: "1px solid #444", paddingTop: "0.75rem", marginBottom: "0.75rem", fontSize: "0.9rem" }}>
                    {/* Subtotal */}
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#b0b0b0", marginBottom: "0.3rem" }}>
                      <span>Subtotal</span>
                      <span>₦{subtotal.toFixed(2)}</span>
                    </div>
                    {/* Discount */}
                    {canApplyDiscount && discountAmount > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", color: "#ef5350", marginBottom: "0.3rem" }}>
                        <span>Discount ({discountType === "percentage" ? `${discountValue}%` : `₦${discountValue} flat`})</span>
                        <span>- ₦{discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {/* Total — always white + cyan accent, impossible to miss */}
                    <div style={{
                      display: "flex", justifyContent: "space-between",
                      fontWeight: 700, fontSize: "1.05rem", color: "#ffffff",
                      borderTop: (canApplyDiscount && discountAmount > 0) ? "1px solid #444" : "none",
                      paddingTop: (canApplyDiscount && discountAmount > 0) ? "0.4rem" : "0.2rem",
                      marginTop: (canApplyDiscount && discountAmount > 0) ? "0.3rem" : 0,
                    }}>
                      <span>Total</span>
                      <span style={{ color: "#4fc3f7" }}>₦{displayTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {cart.length === 0 && <div className="cart-total"><strong style={{ color: "#fff" }}>Total: ₦0.00</strong></div>}

                <button className="checkout-btn cart-checkout-btn" onClick={handleCheckout} disabled={checkoutLoading || cart.length === 0}>
                  {checkoutLoading ? "Processing..." : `Checkout${canApplyDiscount && discountAmount > 0 ? ` — ₦${totalAfterDiscount.toFixed(2)}` : ""}`}
                </button>
              </div>
            </div>
          </>
        )}

        {/* =========================
            STOCK CONFLICTS VIEW
            ========================= */}
        {activeView === "conflicts" && (
          <div className="pos-report-view">
            <h3>🔴 Stock Conflicts</h3>
            <p style={{ color: "#666", marginBottom: "1rem", fontSize: "0.95rem" }}>These offline sales exceeded available stock. The drug was already dispensed to the patient — the sale has been accepted but stock was floored at 0. Do a physical recount, update the stock manually, then dismiss each conflict.</p>
            <ErrorBanner message={conflictsError} onDismiss={() => setConflictsError("")} />
            <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
              <button onClick={() => { setShowResolvedConflicts(false); fetchConflicts(false); }} style={{ padding: "0.5rem 1.25rem", background: !showResolvedConflicts ? "#dc3545" : "#f8f9fa", color: !showResolvedConflicts ? "white" : "#333", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: "600" }}>⚠️ Unresolved {conflictCount > 0 ? `(${conflictCount})` : ""}</button>
              <button onClick={() => { setShowResolvedConflicts(true); fetchConflicts(true); }} style={{ padding: "0.5rem 1.25rem", background: showResolvedConflicts ? "#28a745" : "#f8f9fa", color: showResolvedConflicts ? "white" : "#333", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: "600" }}>✅ Resolved</button>
            </div>
            {conflictsLoading && <p>Loading conflicts...</p>}
            {!conflictsLoading && conflicts.length === 0 && !conflictsError && (
              <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>
                <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{showResolvedConflicts ? "✅" : "🎉"}</div>
                <h4>{showResolvedConflicts ? "No resolved conflicts yet." : "No unresolved stock conflicts!"}</h4>
                <p>{showResolvedConflicts ? "Resolved conflicts will appear here for audit." : "All offline sales were within available stock."}</p>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {conflicts.map((conflict) => (
                <div key={conflict.sale_id} style={{ background: conflict.conflict_resolved ? "#f8fff8" : "#fff8f8", border: `2px solid ${conflict.conflict_resolved ? "#28a745" : "#dc3545"}`, borderRadius: "10px", padding: "1.25rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                    <div>
                      <h4 style={{ margin: "0 0 0.5rem", color: conflict.conflict_resolved ? "#28a745" : "#dc3545" }}>{conflict.conflict_resolved ? "✅ Resolved" : "⚠️ Unresolved"} — Receipt: {conflict.receipt_number}</h4>
                      <p style={{ margin: "0 0 0.25rem", color: "#666", fontSize: "0.9rem" }}>Sale date: {new Date(conflict.created_at).toLocaleString()}</p>
                      <p style={{ margin: "0 0 0.25rem", color: "#666", fontSize: "0.9rem" }}>Total amount: ₦{Number(conflict.total_amount).toFixed(2)}</p>
                      {conflict.conflict_resolved_at && <p style={{ margin: "0", color: "#28a745", fontSize: "0.85rem" }}>Resolved: {new Date(conflict.conflict_resolved_at).toLocaleString()}</p>}
                    </div>
                    {!conflict.conflict_resolved && <button onClick={() => handleResolveConflict(conflict.sale_id, conflict.receipt_number)} disabled={resolvingId === conflict.sale_id} style={{ padding: "0.6rem 1.25rem", background: resolvingId === conflict.sale_id ? "#ccc" : "#28a745", color: "white", border: "none", borderRadius: "6px", cursor: resolvingId === conflict.sale_id ? "not-allowed" : "pointer", fontWeight: "600", whiteSpace: "nowrap" }}>{resolvingId === conflict.sale_id ? "Resolving..." : "✅ Mark Resolved"}</button>}
                  </div>
                  {conflict.conflict_details && conflict.conflict_details.length > 0 && (
                    <div style={{ marginTop: "1rem", borderTop: "1px solid #eee", paddingTop: "1rem" }}>
                      <p style={{ margin: "0 0 0.75rem", fontWeight: "600", color: "#333", fontSize: "0.9rem" }}>Affected items:</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        {conflict.conflict_details.map((detail, idx) => (
                          <div key={idx} style={{ background: "#1e1e2f", border: "1px solid #444", borderRadius: "6px", padding: "0.75rem", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.5rem" }}>
                            <div><span style={{ color: "#aaa", fontSize: "0.8rem" }}>Drug</span><br /><strong style={{ color: "#fff" }}>{detail.name}</strong></div>
                            <div><span style={{ color: "#aaa", fontSize: "0.8rem" }}>Sold offline</span><br /><strong style={{ color: "#fff" }}>{detail.sold} units</strong></div>
                            <div><span style={{ color: "#aaa", fontSize: "0.8rem" }}>Available at sync</span><br /><strong style={{ color: "#ff9800" }}>{detail.available_at_sync} units</strong></div>
                            <div><span style={{ color: "#aaa", fontSize: "0.8rem" }}>Oversold by</span><br /><strong style={{ color: "#dc3545" }}>{detail.oversold_by} units</strong></div>
                            <div><span style={{ color: "#aaa", fontSize: "0.8rem" }}>Stock now</span><br /><strong style={{ color: "#dc3545" }}>0 (floored)</strong></div>
                          </div>
                        ))}
                      </div>
                      <p style={{ margin: "0.75rem 0 0", color: "#856404", fontSize: "0.85rem", background: "#fff3cd", padding: "0.5rem 0.75rem", borderRadius: "4px" }}>💡 Do a physical recount for the above items, update stock manually via Inventory, then mark this conflict as resolved.</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* =========================
            SALES HISTORY VIEW
            ========================= */}
        {activeView === "history" && (
          <div className="pos-report-view">
            <h3>📊 Sales History</h3>
            <ErrorBanner message={historyError} onDismiss={() => setHistoryError("")} />
            <div className="filter-controls">
              <input type="date" className="filter-input" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
              <input type="text" className="filter-input" value={receiptFilter} onChange={(e) => setReceiptFilter(e.target.value)} placeholder="Search receipt number..." />
              <input type="text" className="filter-input" value={drugNameFilter} onChange={(e) => setDrugNameFilter(e.target.value)} placeholder="Search drug name..." />
              <select className="filter-input" value={voidFilter} onChange={(e) => setVoidFilter(e.target.value)}>
                <option value="all">All Receipts</option>
                <option value="active">Active Only</option>
                <option value="voided">Voided Only</option>
              </select>
              <button className="pagination-btn" onClick={handleFilterChange}>🔍 Apply Filters</button>
              <button className="clear-filter-btn" onClick={handleClearFilters}>✖ Clear</button>
            </div>
            {historyLoading && <p>Loading history...</p>}
            {!historyLoading && history.length === 0 && !historyError && <p>No sales found.</p>}
            <div className="history-grid">
              {history.map((sale) => {
                const isVoided = sale.status === "voided";
                const saleDate = new Date(sale.created_at);
                const itemCount = sale.items?.reduce((sum, i) => sum + i.quantity, 0) || 0;
                const hasDiscount = sale.discount_amount && parseFloat(sale.discount_amount) > 0;
                const hasWholesaleItems = sale.items?.some((i) => i.price_type === "wholesale");
                return (
                  <div key={sale.sale_id} className="history-card" style={{ border: `1.5px solid ${isVoided ? "#dc3545" : "#e0e0e0"}`, borderRadius: "10px", overflow: "hidden", background: isVoided ? "#fff8f8" : "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
                    <div style={{ background: isVoided ? "#dc3545" : "#1e1e2f", padding: "0.75rem 1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", letterSpacing: "0.5px" }}>🧾 {sale.receipt_number}</div>
                        <div style={{ color: isVoided ? "#ffcdd2" : "#aaa", fontSize: "0.78rem", marginTop: "2px" }}>
                          {saleDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} · {saleDate.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                        <span style={{ background: isVoided ? "#fff" : "#28a745", color: isVoided ? "#dc3545" : "#fff", padding: "0.25rem 0.65rem", borderRadius: "20px", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}>{isVoided ? "✖ VOIDED" : "✔ COMPLETED"}</span>
                        {hasWholesaleItems && <span style={{ background: "#1565c0", color: "#fff", padding: "0.2rem 0.5rem", borderRadius: "20px", fontSize: "0.7rem", fontWeight: 700 }}>📦 WHOLESALE</span>}
                      </div>
                    </div>
                    <div style={{ padding: "0.65rem 1rem", background: "#f9f9f9", borderBottom: "1px solid #eee", display: "flex", flexWrap: "wrap", gap: "1rem", fontSize: "0.83rem", color: "#555" }}>
                      {sale.sold_by_name && <span>👤 <strong>Sold by:</strong> {sale.sold_by_name}</span>}
                      {isVoided && sale.voided_by_name && <span style={{ color: "#dc3545" }}>✖ <strong>Voided by:</strong> {sale.voided_by_name}</span>}
                      <span>📦 <strong>{itemCount}</strong> unit{itemCount !== 1 ? "s" : ""} · <strong>{sale.items?.length || 0}</strong> line item{(sale.items?.length || 0) !== 1 ? "s" : ""}</span>
                    </div>
                    <div style={{ padding: "0.75rem 1rem" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #eee" }}>
                            <th style={{ textAlign: "left", padding: "0.3rem 0.4rem", color: "#888", fontWeight: 600 }}>Item</th>
                            <th style={{ textAlign: "center", padding: "0.3rem 0.4rem", color: "#888", fontWeight: 600 }}>Qty</th>
                            <th style={{ textAlign: "right", padding: "0.3rem 0.4rem", color: "#888", fontWeight: 600 }}>Unit Price</th>
                            <th style={{ textAlign: "right", padding: "0.3rem 0.4rem", color: "#888", fontWeight: 600 }}>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sale.items?.map((item, idx) => (
                            <tr key={item.inventory_id} style={{ borderBottom: idx < sale.items.length - 1 ? "1px dashed #f0f0f0" : "none" }}>
                              <td style={{ padding: "0.4rem 0.4rem", color: "#333", fontWeight: 500 }}>
                                {item.name}
                                {item.price_type === "wholesale" && <span style={{ marginLeft: "4px", fontSize: "0.68rem", color: "#1565c0", background: "#e3f2fd", padding: "1px 5px", borderRadius: "3px", fontWeight: 600 }}>W/S</span>}
                              </td>
                              <td style={{ textAlign: "center", padding: "0.4rem 0.4rem", color: "#555" }}>{item.quantity}</td>
                              <td style={{ textAlign: "right", padding: "0.4rem 0.4rem", color: "#555" }}>₦{item.subtotal && item.quantity ? (item.subtotal / item.quantity).toFixed(2) : (item.price ?? 0).toFixed(2)}</td>
                              <td style={{ textAlign: "right", padding: "0.4rem 0.4rem", color: "#222", fontWeight: 600 }}>₦{(item.subtotal ?? 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ padding: "0.65rem 1rem", background: "#f4f4f4", borderTop: "1px solid #eee", display: "flex", flexDirection: "column", gap: "0.3rem", fontSize: "0.85rem" }}>
                      {hasDiscount && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#555" }}>Subtotal</span><span style={{ color: "#555" }}>₦{parseFloat(sale.subtotal || sale.total_amount).toFixed(2)}</span></div>}
                      {hasDiscount && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#dc3545" }}>Discount{sale.discount_type === "percentage" ? ` (${sale.discount_value}%)` : ` (₦${sale.discount_value} flat)`}</span><span style={{ color: "#dc3545", fontWeight: 600 }}>- ₦{parseFloat(sale.discount_amount).toFixed(2)}</span></div>}
                      <div style={{ display: "flex", justifyContent: "space-between", borderTop: hasDiscount ? "1px solid #ddd" : "none", paddingTop: hasDiscount ? "0.3rem" : 0, marginTop: hasDiscount ? "0.2rem" : 0 }}>
                        <span style={{ color: "#555" }}>{hasDiscount ? "Total Charged" : "Gross Amount"}</span>
                        <span style={{ fontWeight: 600, color: "#222" }}>₦{parseFloat(sale.total_amount).toFixed(2)}</span>
                      </div>
                      {sale.total_cost !== undefined && (isPharmacyOwner || isPharmacyBranch) && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#555" }}>Cost of Goods</span><span style={{ color: "#e65100" }}>₦{sale.total_cost.toFixed(2)}</span></div>}
                      {sale.total_profit !== undefined && (isPharmacyOwner || isPharmacyBranch) && <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #ddd", paddingTop: "0.3rem", marginTop: "0.2rem" }}><span style={{ fontWeight: 700, color: "#333" }}>Net Profit</span><span style={{ fontWeight: 700, color: sale.total_profit >= 0 ? "#28a745" : "#dc3545" }}>{sale.total_profit >= 0 ? "+" : ""}₦{sale.total_profit.toFixed(2)}</span></div>}
                    </div>
                    <div style={{ padding: "0.65rem 1rem", display: "flex", gap: "0.6rem", justifyContent: "flex-end", borderTop: "1px solid #eee" }}>
                      <button className="reprint-btn" onClick={() => setReceipt({ ...sale })} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.83rem" }}>🖨 Reprint Receipt</button>
                      {!isVoided && (isPharmacyBranch || hasPermission("pos:void_receipt")) && <button className="void-btn" onClick={() => handleVoidReceipt(sale.sale_id, sale.receipt_number)} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.83rem" }}>✖ Void</button>}
                    </div>
                  </div>
                );
              })}
            </div>
            {history.length > 0 && (
              <div className="pagination">
                <button className="pagination-btn" onClick={() => handleHistoryPageChange(historyPage - 1)} disabled={historyPage === 1}>← Previous</button>
                <span className="pagination-info">Page {historyPage} of {Math.ceil(historyTotal / historyLimit) || 1}</span>
                <button className="pagination-btn" onClick={() => handleHistoryPageChange(historyPage + 1)} disabled={history.length < historyLimit}>Next →</button>
              </div>
            )}
          </div>
        )}

        {/* =========================
            SALES REPORT VIEW
            ========================= */}
        {activeView === "reports" && (
          <div className="pos-report-view">
            <h3>📈 Sales Report</h3>
            <div className="report-cards">
              <div className="report-card"><h4>Total Sales</h4><p className="report-value">₦{history.reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0).toFixed(2)}</p></div>
              <div className="report-card"><h4>Total Transactions</h4><p className="report-value">{history.length}</p></div>
              <div className="report-card"><h4>Average Sale</h4><p className="report-value">₦{history.length > 0 ? (history.reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0) / history.length).toFixed(2) : "0.00"}</p></div>
              {history.some((s) => s.discount_amount && parseFloat(s.discount_amount) > 0) && <div className="report-card"><h4>Total Discounts</h4><p className="report-value" style={{ color: "#dc3545" }}>₦{history.reduce((sum, s) => sum + parseFloat(s.discount_amount || 0), 0).toFixed(2)}</p></div>}
              {history.some((s) => s.total_cost !== undefined) && (isPharmacyOwner || isPharmacyBranch) && <div className="report-card"><h4>Total Cost</h4><p className="report-value cost-value">₦{history.reduce((sum, s) => sum + (s.total_cost || 0), 0).toFixed(2)}</p></div>}
              {history.some((s) => s.total_profit !== undefined) && (isPharmacyOwner || isPharmacyBranch) && <div className="report-card"><h4>Total Profit</h4><p className={`report-value ${history.reduce((sum, s) => sum + (s.total_profit || 0), 0) >= 0 ? "profit-positive" : "profit-negative"}`}>₦{history.reduce((sum, s) => sum + (s.total_profit || 0), 0).toFixed(2)}</p></div>}
            </div>
          </div>
        )}

        {/* =========================
            PROFIT ANALYTICS VIEW
            ========================= */}
        {activeView === "profitanalytics" && (
          <div className="pos-report-view">
            <h3>💰 Profit Analytics</h3>
            <ErrorBanner message={profitError} onDismiss={() => setProfitError("")} />
            <div className="profit-period-selector">
              {["daily", "monthly", "yearly", "custom"].map((p) => (
                <button key={p} className={profitPeriod === p ? "period-btn active" : "period-btn"} onClick={() => handleProfitPeriodChange(p)}>
                  {p === "daily" && "📅 Daily"}{p === "monthly" && "📆 Monthly"}{p === "yearly" && "📊 Yearly"}{p === "custom" && "📋 Custom Range"}
                </button>
              ))}
            </div>
            {profitPeriod === "custom" && (
              <div className="custom-date-selector">
                <div className="date-inputs">
                  <div className="date-input-group"><label>Start Date</label><input type="date" className="filter-input" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} /></div>
                  <div className="date-input-group"><label>End Date</label><input type="date" className="filter-input" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} /></div>
                  <button className="pagination-btn search-date-btn" onClick={handleCustomDateSearch}>🔍 Search</button>
                </div>
              </div>
            )}
            {profitLoading && <p>Loading analytics...</p>}
            {!profitLoading && profitData && (
              <>
                <h4 className="profit-period-title">{getPeriodLabel(profitPeriod)}'s Performance</h4>
                <div className="report-cards">
                  <div className="report-card"><h4>Total Revenue</h4><p className="report-value">₦{profitData.totalSales.toFixed(2)}</p></div>
                  <div className="report-card"><h4>Total Cost</h4><p className="report-value cost-value">₦{profitData.totalCost.toFixed(2)}</p></div>
                  <div className="report-card"><h4>Net Profit</h4><p className={`report-value ${profitData.totalProfit >= 0 ? "profit-positive" : "profit-negative"}`}>₦{profitData.totalProfit.toFixed(2)}</p></div>
                  <div className="report-card"><h4>Profit Margin</h4><p className="report-value profit-positive">{profitData.profitMargin.toFixed(1)}%</p></div>
                  <div className="report-card"><h4>Transactions</h4><p className="report-value">{profitData.totalTransactions}</p></div>
                  {profitData.totalDiscount > 0 && <div className="report-card"><h4>Total Discounts</h4><p className="report-value" style={{ color: "#dc3545" }}>₦{profitData.totalDiscount.toFixed(2)}</p></div>}
                  <div className="report-card"><h4>Avg Profit/Sale</h4><p className="report-value profit-positive">₦{profitData.totalTransactions > 0 ? (profitData.totalProfit / profitData.totalTransactions).toFixed(2) : "0.00"}</p></div>
                </div>
                {profitData.topItems.length > 0 && (
                  <div className="top-items-section">
                    <h4>🏆 Top Profitable Items</h4>
                    <div className="top-items-grid">
                      {profitData.topItems.map((item, index) => (
                        <div key={index} className="top-item-card">
                          <div className="top-item-rank">#{index + 1}</div>
                          <div className="top-item-info"><strong>{item.name}</strong><p>Sold: {item.quantity} units</p><p>Revenue: ₦{item.revenue.toFixed(2)}</p><p className="profit-positive">Profit: ₦{item.profit.toFixed(2)}</p></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* =========================
            OUT OF STOCK VIEW
            ========================= */}
        {activeView === "outofstock" && (
          <div className="pos-report-view">
            <h3>⚠️ Out of Stock Items</h3>
            <ErrorBanner message={outOfStockError} onDismiss={() => setOutOfStockError("")} />
            {outOfStockLoading && <p>Loading...</p>}
            {!outOfStockLoading && outOfStock.length === 0 && !outOfStockError && <p>All items are in stock!</p>}
            <div className="outofstock-grid">
              {outOfStock.map((item) => <div key={item.id} className="outofstock-card"><strong>{displayDrugName(item)}</strong><p>Price: ₦{item.price}</p><p className="out-badge">OUT OF STOCK</p></div>)}
            </div>
            {outOfStockTotal > 0 && <div className="pagination"><button className="pagination-btn" onClick={() => handleOutOfStockPageChange(outOfStockPage - 1)} disabled={outOfStockPage === 1}>← Previous</button><span className="pagination-info">Page {outOfStockPage} of {Math.ceil(outOfStockTotal / outOfStockLimit)}</span><button className="pagination-btn" onClick={() => handleOutOfStockPageChange(outOfStockPage + 1)} disabled={outOfStockPage >= Math.ceil(outOfStockTotal / outOfStockLimit)}>Next →</button></div>}
          </div>
        )}

        {/* =========================
            LOW STOCK VIEW
            ========================= */}
        {activeView === "lowstock" && (
          <div className="pos-report-view">
            <h3>🔔 Low Stock Items (≤ {lowStockThreshold} units)</h3>
            <ErrorBanner message={lowStockError} onDismiss={() => setLowStockError("")} />
            {lowStockLoading && <p>Loading...</p>}
            {!lowStockLoading && lowStock.length === 0 && !lowStockError && <p>No low stock items!</p>}
            <div className="outofstock-grid">
              {lowStock.map((item) => <div key={item.id} className="lowstock-card"><strong>{displayDrugName(item)}</strong><p>Price: ₦{item.price}</p><p className="stock-quantity">Stock: <span className="low-stock-value">{item.quantity}</span> units</p><p className="low-badge">LOW STOCK</p></div>)}
            </div>
            {lowStockTotal > 0 && <div className="pagination"><button className="pagination-btn" onClick={() => handleLowStockPageChange(lowStockPage - 1)} disabled={lowStockPage === 1}>← Previous</button><span className="pagination-info">Page {lowStockPage} of {Math.ceil(lowStockTotal / lowStockLimit)}</span><button className="pagination-btn" onClick={() => handleLowStockPageChange(lowStockPage + 1)} disabled={lowStockPage >= Math.ceil(lowStockTotal / lowStockLimit)}>Next →</button></div>}
          </div>
        )}

        {/* =========================
            EXPIRING SOON VIEW
            ========================= */}
        {activeView === "expiring" && (
          <div className="pos-report-view">
            <h3>⏰ Expiring Soon (Next {expiryThreshold} days)</h3>
            <ErrorBanner message={expiringSoonError} onDismiss={() => setExpiringSoonError("")} />
            {expiringSoonLoading && <p>Loading...</p>}
            {!expiringSoonLoading && expiringSoon.length === 0 && !expiringSoonError && <div className="info-message"><p>No items expiring in the next {expiryThreshold} days.</p><p className="info-note">💡 Make sure your inventory items have expiry dates set.</p></div>}
            <div className="outofstock-grid">
              {expiringSoon.map((item) => {
                const expiryDateValue = item.expiration_date || item.expiry_date;
                const daysLeft = calculateDaysUntilExpiry(expiryDateValue);
                const statusColor = getExpiryStatusColor(daysLeft);
                return (
                  <div key={item.id} className="expiring-card" style={{ borderLeftColor: statusColor }}>
                    <strong>{displayDrugName(item)}</strong>
                    <p>Price: ₦{item.price}</p>
                    <p className="stock-quantity">Stock: {item.quantity} units</p>
                    <p className="expiry-date">Expires: {new Date(expiryDateValue).toLocaleDateString()}</p>
                    <p className="expiry-badge" style={{ backgroundColor: statusColor }}>{daysLeft} {daysLeft === 1 ? "day" : "days"} left</p>
                  </div>
                );
              })}
            </div>
            {expiringSoonTotal > 0 && <div className="pagination"><button className="pagination-btn" onClick={() => handleExpiringSoonPageChange(expiringSoonPage - 1)} disabled={expiringSoonPage === 1}>← Previous</button><span className="pagination-info">Page {expiringSoonPage} of {Math.ceil(expiringSoonTotal / expiringSoonLimit)}</span><button className="pagination-btn" onClick={() => handleExpiringSoonPageChange(expiringSoonPage + 1)} disabled={expiringSoonPage >= Math.ceil(expiringSoonTotal / expiringSoonLimit)}>Next →</button></div>}
          </div>
        )}
      </div>

      {receipt && <ReceiptModal sale={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
};

export default POS;